import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

type LegalSection = {
  title: string;
  description?: string;
  body?: string[];
  bullets?: string[];
};

type LegalPageProps = {
  title: string;
  description: string;
  lastUpdated: string;
  path: string;
  sections: LegalSection[];
};

const getCanonical = (path: string) => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${path}`;
  }
  return `https://jobs.lighthouse.careers${path}`;
};

export function LegalPage({ title, description, lastUpdated, path, sections }: LegalPageProps) {
  const canonical = getCanonical(path);

  return (
    <main className="min-h-screen bg-[#030712] py-16 px-4 text-white" aria-labelledby="legal-heading">
      <Helmet>
        <title>{`${title} - Lighthouse Jobs`}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href={canonical} />
      </Helmet>

      <div className="mx-auto max-w-4xl space-y-10">
        <header className="space-y-4 text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-[#f7ef8a]">Lighthouse policies</p>
          <h1 id="legal-heading" className="text-4xl font-semibold">{title}</h1>
          <p className="text-base text-gray-300">{description}</p>
          <p className="text-sm text-gray-400">Last updated: {lastUpdated}</p>
        </header>

        <div className="space-y-8 rounded-3xl border border-white/10 bg-white/5 p-8">
          {sections.map((section) => (
            <section key={section.title} className="space-y-4">
              <h2 className="text-2xl font-semibold text-white">{section.title}</h2>
              {section.description && <p className="text-gray-300">{section.description}</p>}
              {section.body?.map((paragraph) => (
                <p key={paragraph} className="text-gray-300">
                  {paragraph}
                </p>
              ))}
              {section.bullets && section.bullets.length > 0 && (
                <ul className="list-disc space-y-2 pl-6 text-sm text-gray-300">
                  {section.bullets.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <div className="rounded-3xl border border-white/10 bg-[#0c101f] p-6 text-center">
          <p className="text-lg font-medium text-white">Need something clarified?</p>
          <p className="mt-2 text-sm text-gray-300">
            Our team can explain how these policies apply to your candidate, employer, or admin workspace.
          </p>
          <Link
            to="/contact"
            className="mt-4 inline-flex items-center justify-center rounded-xl bg-gold-gradient px-6 py-2 text-sm font-semibold text-black transition hover:brightness-110"
          >
            Contact Lighthouse
          </Link>
        </div>
      </div>
    </main>
  );
}
